import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getAllTicket } from '../api/ticketApi';
import DOMPurify from 'dompurify';

const BATCH_SIZE = 10;

const getStatusStyle = (status) => {
  switch (status) {
    case 'Open':
      return { bg: '#dcfce7', text: '#15803d', dot: '#22c55e' };
    case 'Closed':
      return { bg: '#f1f5f9', text: '#1e293b', dot: '#1e293b' };
    default:
      return { bg: '#f1f5f9', text: '#374151', dot: '#9ca3af' };
  }
};

const AllTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [displayedTickets, setDisplayedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();

  useEffect(() => {
    const workspace_id = sessionStorage.getItem('login_workspaces_id');

    const fetchTickets = async () => {
      try {
        const data = await getAllTicket(workspace_id);
        setTickets(data.results);
        setDisplayedTickets(data.results.slice(0, BATCH_SIZE));
        setHasMore(data.results.length > BATCH_SIZE);
      } catch (err) {
        setError('Failed to fetch tickets.');
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  const loadMoreTickets = () => {
    const nextIndex = displayedTickets.length;
    const nextBatch = tickets.slice(nextIndex, nextIndex + BATCH_SIZE);
    setDisplayedTickets((prev) => [...prev, ...nextBatch]);
    if (nextIndex + BATCH_SIZE >= tickets.length) setHasMore(false);
  };

  const lastTicketRef = useCallback(
    (node) => {
      if (loading || !hasMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadMoreTickets();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore],
  );

  return (
    <div className="w-full max-w-full bg-white shadow-sm rounded-lg mt-5">
      <div
        className="flex items-center space-x-3 mb-3 pl-3 pt-3 pb-2 cursor-pointer"
        onClick={() => navigate('/contactsupport')}
      >
        <img src="./arrow-left.svg" alt="back" />
        <span className="text-lg font-bold">Existing Query</span>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {displayedTickets.map((ticket, index) => {
        const isLast = index === displayedTickets.length - 1;
        const style = getStatusStyle(ticket.status_display);

        return (
          <div
            ref={isLast ? lastTicketRef : null}
            key={ticket.id || index}
            className="flex items-center justify-between px-6 py-4 border-b last:border-b-0 mb-4"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#F6F8FC] text-center flex items-center justify-center font-bold text-[#0B214E]">
                {ticket.title?.charAt(0).toUpperCase() || 'T'}
              </div>

              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm sm:text-base text-black">
                    {ticket?.title}{' '}
                    <span className="text-xs text-[#707070]">
                      {ticket?.createdDate || ''}
                    </span>
                  </h4>

                  <span
                    className="flex items-center gap-1 text-xs font-semibold px-2 py-[2px] rounded-full"
                    style={{ backgroundColor: style.bg, color: style.text }}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: style.dot }}
                    />
                    {ticket.status_display}
                  </span>
                </div>

                <div className="text-sm text-[#000000]">
                  <span className="font-semibold">Description: </span>
                  <div className='flex '>
                    <span className="pt-2 pr-2">●</span>
                    <span className='pt-2'
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          (ticket.description || '').length > 300
                            ? ticket.description.substring(0, 300) + '...'
                            : ticket.description,
                          {
                            ALLOWED_TAGS: [
                              'b',
                              'i',
                              'u',
                              'a',
                              'br',
                              'strong',
                              'em',
                              'p',
                              'ul',
                              'ol',
                              'li',
                            ],
                            ALLOWED_ATTR: ['href', 'target', 'rel'],
                          },
                        ),
                      }}
                    />
                  </div>
                </div>

                <p className="text-xs text-[#8D8D8D] mt-1">{ticket.subtitle}</p>
              </div>
            </div>

            <button
              onClick={() => navigate(`/ticketdetails/${ticket.id}`)}
              className="border border-[#000000] text-[#0B214E] font-medium text-sm px-10 py-1.5 rounded-md"
            >
              View
            </button>
          </div>
        );
      })}

      {loading && (
        <p className="text-center py-6 text-sm text-gray-500">Loading...</p>
      )}
      {!hasMore && !loading && (
        <p className="text-center py-6 text-sm text-gray-400">
          No more tickets to load.
        </p>
      )}
    </div>
  );
};

export default AllTickets;